import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, ChevronRight, ShoppingCart, Wheat } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api, { platformAPI } from '@/lib/api';
import useManagedClusters from '@/hooks/useManagedClusters';
import HomeShell from '@/components/home/HomeShell';
import HomeTiles from '@/components/home/HomeTiles';
import HomeLatestDeals from '@/components/home/HomeLatestDeals';
import HomeBigButtons from '@/components/home/HomeBigButtons';
import { HomeSearchBar, HomeStakeholderStrip } from '@/components/home/HomeBlocks';

// People circles, search, the two big Sell / Buy buttons, latest deals,
// then the tiles. (No "My statistics" box: beta feedback.)
export default function FarmerHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [deals, setDeals] = useState([]);
  const [platform, setPlatform] = useState({});
  const [searchQ, setSearchQ] = useState('');
  // Clusters / associations the support team made this farmer manager of.
  const managed = useManagedClusters(user);

  const loadData = useCallback(async () => {
    const [dealRes, platRes] = await Promise.allSettled([
      api.get(`/deals/for-user/${user?.id}`, { params: { sort: 'newest', limit: 10 } }),
      platformAPI.getStatistics(),
    ]);
    if (dealRes.status === 'fulfilled') {
      const rows = dealRes.value.data;
      setDeals(Array.isArray(rows) ? rows : rows?.items || []);
    }
    if (platRes.status === 'fulfilled') setPlatform(platRes.value.data || {});
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    navigate(q ? `/marketplace?search=${encodeURIComponent(q)}` : '/marketplace');
  };

  return (
    <HomeShell>
      <HomeStakeholderStrip platform={platform} t={t} className="gap-2" />
      <HomeSearchBar
        value={searchQ}
        onChange={setSearchQ}
        onSubmit={onSearchSubmit}
        testId="farmer-home-search-input"
      />
      {/* The two main jobs, side by side: sell what the farm produces
          (to offtakers, PR 67) and buy inputs for the farm. */}
      <HomeBigButtons buttons={[
        { icon: Wheat, label: t('farmerHome.sellProducts'), onClick: () => navigate('/sell'), testId: 'farmer-sell-produce' },
        { icon: ShoppingCart, label: t('farmerHome.buyProducts'), onClick: () => navigate('/marketplace'), testId: 'farmer-buy-products' },
      ]} />
      {managed.length > 0 && (
        <button type="button" onClick={() => navigate('/cluster')}
                className="w-full flex items-center gap-3 rounded-2xl bg-white shadow-sm px-4 py-3 mt-3 mb-1 text-left"
                data-testid="farmer-my-cluster">
          <span className="w-11 h-11 rounded-xl bg-purple-100 text-purple-800 grid place-items-center shrink-0">
            <BadgeCheck className="w-6 h-6" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-semibold break-words">
              {managed.length === 1
                ? t('produceMarket.cluster.homeCard', { name: managed[0].name })
                : t('produceMarket.cluster.homeCardMany', { n: managed.length })}
            </span>
            <span className="block text-sm text-gray-600">{t('produceMarket.cluster.homeCardSub')}</span>
          </span>
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
        </button>
      )}
      <HomeLatestDeals deals={deals} />
      <HomeTiles role="farmer" />
    </HomeShell>
  );
}

